import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getActivityLogs, getUsers } from "../services/api";
import { ICONS } from "../icons";
import SearchInput from "../components/UI/SearchInput";
import SelectBox from "../components/UI/SelectBox";
import FloatingInput from "../components/UI/FloatingInput";
import { notify } from "../utils/notify";
import DataTable from "../components/UI/DataTable";
import PageHeader from "../components/UI/PageHeader";
import { useTranslation } from "../context/I18nContext";

// Logs.js is a straight, read-only audit trail viewer — every ActivityLog
// row (see ListActivityLogsHandler's own doc comment): login/logout, plus
// whatever routes an admin is watching from the Events screen. There's
// nothing to add/edit/delete here on purpose, so unlike every other list
// page in this app, DataTable's Actions column is passed a no-op
// (renderActions={() => null}) rather than Edit/Delete buttons.
export default function Logs() {
  const { t } = useTranslation("logs");
  const { t: tCommon } = useTranslation("common");
  const [searchParams, setSearchParams] = useSearchParams();
  const currentPage = parseInt(searchParams.get("page") || "1");
  const userFilter = searchParams.get("user_id") || "";
  const statusFilter = searchParams.get("status") || "";
  const searchTerm = searchParams.get("search") || "";
  const dateFrom = searchParams.get("date_from") || "";
  const dateTo = searchParams.get("date_to") || "";

  const [users, setUsers] = useState([]);
  const [data, setData] = useState({ items: [], total: 0, serverLimit: 10 });
  const [sortConfig, setSortConfig] = useState({ key: "created_at", direction: "desc" });

  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const fetchLogs = async () => {
    try {
      const params = { page: currentPage };
      if (userFilter) params.user_id = userFilter;
      if (statusFilter) params.status = statusFilter;
      if (searchTerm) params.search = searchTerm;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const response = await getActivityLogs(token, params);
      setData({
        items: response.data || [],
        total: response.meta?.total || 0,
        serverLimit: response.meta?.limit || 10,
      });
    } catch (err) {
      notify.error(t("notify_load_failed", "Failed to load system logs"));
    }
  };

  useEffect(() => {
    // limit=1000: just enough users for a filter dropdown, same reasoning
    // AddEditGeoZone.js's CITIES_OPTIONS_LIMIT gives for its own "load
    // everything into a <Select>, no search-as-you-type yet" tradeoff.
    getUsers(token, { limit: 1000 })
      .then((res) => setUsers(res.data || res || []))
      .catch(() => notify.error(tCommon("error_generic", "Something went wrong")));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { fetchLogs(); }, [currentPage, userFilter, statusFilter, searchTerm, dateFrom, dateTo]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateParams = (newParams) => {
    const current = Object.fromEntries(searchParams.entries());
    setSearchParams({ ...current, ...newParams });
  };

  const userOptions = [
    { value: "", label: t("filter_user_all_option", "All users") },
    ...users.map((u) => ({ value: String(u.id), label: u.username })),
  ];
  const statusOptions = [
    { value: "", label: t("filter_status_all_option", "All statuses") },
    { value: "Attempted", label: t("status_attempted", "Attempted") },
    { value: "Success", label: t("status_success", "Success") },
  ];

  // Client-side only — the backend already returns rows newest-first
  // (Order("created_at DESC")), and every column here is either free text
  // or a fixed timestamp, so re-sorting a single already-paginated page
  // client-side (mirrors every other list page's sortConfig) is enough;
  // there's no server-side "sort by" param to wire up.
  const sortedItems = [...data.items].sort((a, b) => {
    let valA = sortConfig.key === "user" ? a.user?.username : a[sortConfig.key];
    let valB = sortConfig.key === "user" ? b.user?.username : b[sortConfig.key];
    if (typeof valA === "string") valA = valA.toLowerCase();
    if (typeof valB === "string") valB = valB.toLowerCase();
    if (valA < valB) return sortConfig.direction === "asc" ? -1 : 1;
    if (valA > valB) return sortConfig.direction === "asc" ? 1 : -1;
    return 0;
  });

  const handleSort = (key) => {
    const direction = sortConfig.key === key && sortConfig.direction === "asc" ? "desc" : "asc";
    setSortConfig({ key, direction });
  };

  const totalPages = Math.ceil(data.total / data.serverLimit) || 1;

  const columns = [
    { key: "id", label: tCommon("table_col_id", "ID"), className: "text-gray-400 font-mono" },
    {
      key: "user",
      label: t("col_user", "User"),
      className: "font-bold text-gray-700",
      render: (row) => row.user?.username || "—",
    },
    { key: "event_name", label: t("col_event", "Event"), className: "text-gray-600 font-mono text-xs" },
    { key: "details", label: t("col_details", "Details"), className: "text-gray-500" },
    {
      key: "status",
      label: t("col_status", "Status"),
      render: (row) => (
        <span
          className={`px-2 py-0.5 rounded text-[10px] font-bold border ${
            row.status === "Success"
              ? "bg-green-50 text-green-600 border-green-100"
              : "bg-amber-50 text-amber-600 border-amber-100"
          }`}
        >
          {row.status === "Success" ? t("status_success", "Success") : t("status_attempted", "Attempted")}
        </span>
      ),
    },
    { key: "ip_address", label: t("col_ip", "IP Address"), className: "text-gray-500 font-mono text-xs" },
    {
      key: "created_at",
      label: t("col_created_at", "Time"),
      className: "text-gray-500 text-xs",
      render: (row) => (row.created_at ? new Date(row.created_at).toLocaleString() : "—"),
    },
  ];

  return (
    <div className="p-4 md:p-6 max-w-[1600px] mx-auto transition-all duration-300">
      <PageHeader className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-6 border-b border-gray-100 pb-6">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate("/dashboard")} className="p-2 hover:bg-gray-100 rounded-xl transition-colors text-gray-400">
            <ICONS.ChevronLeft className="w-6 h-6 rtl:rotate-180" />
          </button>
          <div className="flex items-center justify-center min-w-[48px] h-[48px] text-[var(--primary-color)]">
            <ICONS.Logs size={40} className="shrink-0" />
          </div>
          <div>
            <h2 className="text-xl md:text-2xl font-bold" style={{ color: "var(--primary-color)" }}>{t("page_title", "System Logs")}</h2>
            <p className="text-gray-400 text-xs md:text-sm font-medium">{t("page_subtitle", "Every login, logout, and watched event, newest first")}</p>
          </div>
        </div>

        <SearchInput
          value={searchTerm}
          onChange={(val) => updateParams({ search: val, page: 1 })}
          placeholder={t("search_placeholder", "Search event or details...")}
        />
      </PageHeader>

      <div className="mb-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <SelectBox
          label={t("filter_user_label", "Filter by user")}
          options={userOptions}
          value={userFilter}
          onChange={(val) => updateParams({ user_id: val, page: 1 })}
        />
        <SelectBox
          label={t("filter_status_label", "Filter by status")}
          options={statusOptions}
          value={statusFilter}
          onChange={(val) => updateParams({ status: val, page: 1 })}
        />
        <FloatingInput
          label={t("filter_date_from_label", "From date")}
          type="date"
          value={dateFrom}
          onChange={(e) => updateParams({ date_from: e.target.value, page: 1 })}
        />
        <FloatingInput
          label={t("filter_date_to_label", "To date")}
          type="date"
          value={dateTo}
          onChange={(e) => updateParams({ date_to: e.target.value, page: 1 })}
        />
      </div>

      <DataTable
        columns={columns}
        data={sortedItems}
        sortConfig={sortConfig}
        onSort={handleSort}
        renderActions={() => null}
        mobileRender={(row) => (
          <div className="flex flex-col gap-1">
            <div className="flex justify-between items-center">
              <span className="text-[10px] text-gray-400 font-mono">#{row.id}</span>
              <span className="text-[10px] text-gray-400">{row.created_at ? new Date(row.created_at).toLocaleString() : "—"}</span>
            </div>
            <h3 className="font-bold text-gray-800">{row.event_name}</h3>
            <p className="text-xs text-gray-500">{row.user?.username || "—"} · {row.details}</p>
          </div>
        )}
        paginationConfig={{
          currentPage,
          totalPages,
          onPageChange: (p) => updateParams({ page: p }),
          totalItems: data.total,
        }}
      />
    </div>
  );
}
